import { apiRequest, apiRoutes } from '../../../../api';
// Shared utilities for Step5_GeoScope components

export function pointInPolygon(point, polygon) {
    const [py, px] = point;
    let inside = false;
    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const [iy, ix] = polygon[i];
        const [jy, jx] = polygon[j];
        if (((iy > py) !== (jy > py)) && px < ((jx - ix) * (py - iy)) / (jy - iy) + ix) inside = !inside;
    }
    return inside;
}

export function getCentroid(boundary) {
    if (!boundary) return null;
    const geom = typeof boundary === 'string'
        ? (() => { try { return JSON.parse(boundary); } catch { return null; } })() : boundary;
    if (!geom) return null;
    const coords = geom.type === 'MultiPolygon' ? geom.coordinates[0][0]
        : geom.type === 'Polygon' ? geom.coordinates[0] : null;
    if (!coords?.length) return null;
    let lat = 0, lng = 0;
    coords.forEach(([lo, la]) => { lng += lo; lat += la; });
    return [lat / coords.length, lng / coords.length];
}

export function toGeometry(boundary) {
    if (!boundary) return null;
    if (typeof boundary === 'string') { try { return JSON.parse(boundary); } catch { return null; } }
    return boundary;
}

/** Ray-cast: point [lng, lat] inside a GeoJSON ring [[lng,lat],...] */
function pointInRingLngLat(lngLat, ring) {
    const [px, py] = lngLat;
    let inside = false;
    for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
        const [xi, yi] = ring[i], [xj, yj] = ring[j];
        if (((yi > py) !== (yj > py)) && px < ((xj - xi) * (py - yi) / (yj - yi) + xi))
            inside = !inside;
    }
    return inside;
}

/** Check if a [lat,lng] point is inside any polygon of a MultiPolygon (GeoJSON [lng,lat] rings). */
export function centroidInMultiPolygon(latLng, multiPolygon) {
    if (!multiPolygon?.coordinates) return false;
    const [lat, lng] = latLng;
    return multiPolygon.coordinates.some(poly =>
        poly.some(ring => {
            let inside = false;
            for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
                const [ri, rlat_i] = ring[i], [rj, rlat_j] = ring[j];
                if (((rlat_i > lat) !== (rlat_j > lat)) &&
                    lng < ((rj - ri) * (lat - rlat_i) / (rlat_j - rlat_i) + ri)) inside = !inside;
            }
            return inside;
        })
    );
}

/** Build a MultiPolygon GeoJSON from nbr boundaries + iso rings + geo-analysis rings. */
export function buildSearchBoundaries(nbrBoundaries, isoPolygons, geoAnalysis) {
    const polygons = [];
    nbrBoundaries.forEach(geom => {
        if (!geom) return;
        if (geom.type === 'Polygon') polygons.push(geom.coordinates);
        else if (geom.type === 'MultiPolygon') geom.coordinates.forEach(p => polygons.push(p));
    });
    isoPolygons.forEach(ring => {
        if (ring?.length > 3) {
            const closed = ring[0][0] === ring[ring.length - 1][0] ? ring : [...ring, ring[0]];
            polygons.push([closed]);
        }
    });
    if (geoAnalysis?.coordinates) {
        geoAnalysis.coordinates.forEach(poly => polygons.push(poly));
    }
    if (!polygons.length) return null;
    return { type: 'MultiPolygon', coordinates: polygons };
}

/**
 * Extract all outer rings (first ring of each polygon) from a source as
 * flat arrays of [lng,lat] points.
 */
function extractRings(nbrBoundaries, isoPolygons, geoAnalysis) {
    const rings = [];
    nbrBoundaries.forEach(geom => {
        if (!geom) return;
        if (geom.type === 'Polygon') rings.push(geom.coordinates[0]);
        else if (geom.type === 'MultiPolygon') geom.coordinates.forEach(p => rings.push(p[0]));
    });
    isoPolygons.forEach(ring => {
        if (ring?.length > 3) rings.push(ring);
    });
    if (geoAnalysis?.coordinates) {
        geoAnalysis.coordinates.forEach(poly => rings.push(poly[0]));
    }
    return rings;
}

/**
 * Compute the approximate geometric intersection of all three active boundary
 * sources. Returns a MultiPolygon GeoJSON geometry (or null if no active sources).
 *
 * Algorithm: take all rings from each active source and keep only the points of
 * each ring that lie inside ALL rings of every OTHER active source. Rings that
 * produce ≥ 4 surviving points become polygons in the result.
 */
export function buildIntersectionBoundary(nbrBoundaries, isoPolygons, geoAnalysis) {
    // Collect per-source ring groups
    const sourceGroups = [];

    const nbrRings = extractRings(nbrBoundaries, [], null);
    if (nbrRings.length) sourceGroups.push(nbrRings);

    const isoRings = extractRings([], isoPolygons, null);
    if (isoRings.length) sourceGroups.push(isoRings);

    const geoRings = extractRings([], [], geoAnalysis);
    if (geoRings.length) sourceGroups.push(geoRings);

    if (!sourceGroups.length) return null;
    if (sourceGroups.length === 1) {
        // Only one source active — show it as-is
        const polys = sourceGroups[0].map(ring => [ring]);
        return { type: 'MultiPolygon', coordinates: polys };
    }

    // For each ring in the first group, keep points inside at least one ring
    // of every other group → gives the intersection shape
    const resultPolys = [];
    sourceGroups[0].forEach(ring => {
        const otherGroups = sourceGroups.slice(1);
        const filtered = ring.filter(pt =>
            otherGroups.every(group =>
                group.some(otherRing => pointInRingLngLat(pt, otherRing))
            )
        );
        if (filtered.length >= 4) {
            const closed = filtered[filtered.length - 1][0] === filtered[0][0]
                ? filtered : [...filtered, filtered[0]];
            resultPolys.push([closed]);
        }
    });

    if (!resultPolys.length) return null;
    return { type: 'MultiPolygon', coordinates: resultPolys };
}

/**
 * Returns true if a neighborhood GeoJSON geometry (Polygon or MultiPolygon)
 * geometrically overlaps a MultiPolygon intersection result.
 * "Overlaps" = any vertex of intersectionMP is inside the neighborhood,
 *              OR any vertex of the neighborhood is inside intersectionMP.
 */
export function neighborhoodOverlapsIntersection(nbrGeom, intersectionMP) {
    if (!nbrGeom || !intersectionMP?.coordinates?.length) return false;

    // Collect neighborhood outer rings [[lng,lat],...]
    const nbrRings = [];
    if (nbrGeom.type === 'Polygon') nbrRings.push(nbrGeom.coordinates[0]);
    else if (nbrGeom.type === 'MultiPolygon') nbrGeom.coordinates.forEach(p => nbrRings.push(p[0]));
    if (!nbrRings.length) return false;

    // Collect intersection outer rings
    const intRings = intersectionMP.coordinates.map(p => p[0]);

    // Test 1: any intersection vertex inside any neighborhood ring
    for (const intRing of intRings) {
        for (const pt of intRing) {
            if (nbrRings.some(nr => pointInRingLngLat(pt, nr))) return true;
        }
    }

    // Test 2: any neighborhood vertex inside any intersection ring
    for (const nbrRing of nbrRings) {
        for (const pt of nbrRing) {
            if (intRings.some(ir => pointInRingLngLat(pt, ir))) return true;
        }
    }

    return false;
}

/** Build MultiPolygon GeoJSON from nbrBoundaries array */
function nbrToGeoJSON(nbrBoundaries) {
    const polys = [];
    nbrBoundaries.forEach(geom => {
        if (!geom) return;
        if (geom.type === 'Polygon') polys.push(geom.coordinates);
        else if (geom.type === 'MultiPolygon') geom.coordinates.forEach(p => polys.push(p));
    });
    return polys.length ? { type: 'MultiPolygon', coordinates: polys } : null;
}

/** Build MultiPolygon GeoJSON from isoPolygons (array of [lng,lat] rings) */
function isoToGeoJSON(isoPolygons) {
    const polys = [];
    isoPolygons.forEach(ring => {
        if (!ring || ring.length < 4) return;
        const closed = ring[0][0] === ring[ring.length - 1][0] ? ring : [...ring, ring[0]];
        polys.push([closed]);
    });
    return polys.length ? { type: 'MultiPolygon', coordinates: polys } : null;
}

/**
 * Call the backend intersection API and return the resulting MultiPolygon GeoJSON,
 * or fall back to a client-side union if the API fails.
 * Returns null if no active sources.
 */
export async function fetchIntersectionFromAPI(nbrBoundaries, isoPolygons, geoAnalysis) {
    const body = {};
    const nbr = nbrToGeoJSON(nbrBoundaries);
    const iso = isoToGeoJSON(isoPolygons);
    if (nbr) body.nbr = nbr;
    if (iso) body.iso = iso;
    if (geoAnalysis) body.geo = geoAnalysis;

    const sourceCount = Object.keys(body).length;
    if (sourceCount === 0) return null;
    if (sourceCount === 1) return body.nbr || body.iso || body.geo;

    try {
        const r = await apiRequest(apiRoutes.geo.polygonIntersection, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + (localStorage.getItem('access_token') || ''),
            },
            body: JSON.stringify(body),
        });
        const data = await r.json();
        return data.intersection || buildSearchBoundaries(nbrBoundaries, isoPolygons, geoAnalysis);
    } catch {
        return buildSearchBoundaries(nbrBoundaries, isoPolygons, geoAnalysis);
    }
}
